import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuthFetch } from '../hooks/useAuthFetch.js';
import { usePlatform } from './PlatformContext.js';

const SERVER_URL = import.meta.env.VITE_SERVER_URL ?? '';

/**
 * `none` — a tenant the server has never seen, read from inside a meeting.
 * No trial exists yet; it starts when the tenant first uploads a document.
 */
export type EntitlementStatus = 'trialing' | 'active' | 'past_due' | 'canceled' | 'comped' | 'none';

export interface Entitlement {
  status: EntitlementStatus;
  /**
   * True when another organisation hosts this meeting (it uploaded the first
   * document). Guests are offered Annoteer for their own team, not a trial.
   */
  isGuest?: boolean;
  entitled: boolean;
  canExtract: boolean;
  isTrial: boolean;
  trialEndsAt: string;
  trialDaysLeft: number;
  /** null when not on trial (i.e. unlimited). */
  trialExtractionsLeft: number | null;
  /** False when the server has billing gates switched off entirely. */
  billingEnabled: boolean;
  /** True when the server answered from its fail-open path. */
  degraded?: boolean;
}

interface BillingContextValue {
  entitlement: Entitlement | null;
  /** True until the first lookup resolves. */
  isLoading: boolean;
  /** Re-read entitlement from the server (e.g. after returning from Checkout). */
  refresh: () => Promise<void>;
  /** Start Stripe Checkout in an external browser. Resolves once opened. */
  startCheckout: (quantity?: number) => Promise<void>;
  /** Open the Stripe billing portal in an external browser. */
  openBillingPortal: () => Promise<void>;
  /** Last checkout/portal error, for surfacing in the UI. */
  actionError: string | null;
  isStartingCheckout: boolean;
}

const BillingContext = createContext<BillingContextValue | null>(null);

/**
 * Dev/preview override: `?billingPreview=trial|ending|quota|expired|past_due|active`
 * synthesises an entitlement instead of calling the server.
 *
 * Locally the dev tenant is permanently comped and BILLING_ENABLED is off, so
 * the trial UI can never appear on its own — this is the only practical way to
 * eyeball it without a real lapsed tenant. Same convention as the existing
 * `?showLightMeetingsBanner=1` flag. Read-only: it never touches the server,
 * and the server's gates remain the real enforcement either way.
 */
function previewEntitlement(): Entitlement | null {
  if (typeof window === 'undefined') return null;
  const preset = new URLSearchParams(window.location.search).get('billingPreview');
  if (!preset) return null;

  const base: Entitlement = {
    status: 'trialing',
    entitled: true,
    canExtract: true,
    isTrial: true,
    trialEndsAt: new Date(Date.now() + 14 * 86_400_000).toISOString(),
    trialDaysLeft: 14,
    trialExtractionsLeft: 10,
    billingEnabled: true,
  };

  switch (preset) {
    case 'trial':
      return base;
    case 'ending':
      return { ...base, trialDaysLeft: 2, trialExtractionsLeft: 3 };
    case 'quota':
      return { ...base, trialDaysLeft: 9, trialExtractionsLeft: 0, canExtract: false };
    case 'expired':
      return {
        ...base,
        status: 'canceled',
        entitled: false,
        canExtract: false,
        isTrial: false,
        trialDaysLeft: 0,
        trialExtractionsLeft: null,
      };
    case 'past_due':
      return { ...base, status: 'past_due', isTrial: false, trialExtractionsLeft: null };
    // A participant from another organisation that has never used Annoteer.
    case 'guest':
      return {
        ...base,
        status: 'none',
        isTrial: false,
        trialDaysLeft: 0,
        trialExtractionsLeft: null,
        isGuest: true,
      };
    // A healthy paying customer: no banner at all, but the header must still
    // offer a way into the billing portal to cancel or change seats.
    case 'active':
      return { ...base, status: 'active', isTrial: false, trialExtractionsLeft: null };
    default:
      return null;
  }
}

export function BillingProvider({ children }: { children: ReactNode }) {
  const { authFetch, isAuthenticated } = useAuthFetch();
  const { adapter, context } = usePlatform();
  // Sent with every lookup so the server can tell a guest from the host, and
  // so that opening the panel in a meeting never starts a trial by itself.
  const meetingId = context?.meetingId;

  const [entitlement, setEntitlement] = useState<Entitlement | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [actionError, setActionError] = useState<string | null>(null);
  const [isStartingCheckout, setIsStartingCheckout] = useState(false);

  // Guards a refresh-on-focus from firing before the user is authenticated.
  const authedRef = useRef(isAuthenticated);
  authedRef.current = isAuthenticated;

  const refresh = useCallback(async () => {
    const preview = previewEntitlement();
    if (preview) {
      setEntitlement(preview);
      setIsLoading(false);
      return;
    }
    if (!isAuthenticated) return;
    try {
      const query = meetingId ? `?meetingId=${encodeURIComponent(meetingId)}` : '';
      const res = await authFetch(`${SERVER_URL}/api/billing/entitlement${query}`);
      if (!res.ok) throw new Error(`Entitlement lookup failed (${res.status})`);
      setEntitlement((await res.json()) as Entitlement);
    } catch (err) {
      // Never block the app on a billing read. The server's gates are the
      // real enforcement point; the UI just loses its banner.
      console.warn('Entitlement lookup failed', err);
    } finally {
      setIsLoading(false);
    }
    // authFetch MUST be in the dep array. It changes identity when the auth
    // token arrives, and a callback that captured the pre-auth version throws
    // "no auth token". This exact stale-closure bug has bitten this codebase
    // twice already (DocumentUpload, SyncContext).
  }, [authFetch, isAuthenticated, meetingId]);

  useEffect(() => {
    void refresh();
  }, [isAuthenticated, refresh]);

  // Checkout happens in a different browser window, so there is no callback
  // into this app when payment completes. Re-reading on focus is what turns a
  // completed purchase into an unlocked UI without the user reloading.
  useEffect(() => {
    function onFocus() {
      if (authedRef.current) void refresh();
    }
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [refresh]);

  const openExternal = useCallback(
    async (path: string, body: unknown, failureMessage: string) => {
      setActionError(null);
      const res = await authFetch(`${SERVER_URL}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body ?? {}),
      });

      if (!res.ok) {
        const detail = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new Error(detail?.error ?? failureMessage);
      }

      const { url } = (await res.json()) as { url?: string };
      if (!url) throw new Error(failureMessage);

      // Must leave the Teams iframe — Stripe refuses to render inside it.
      await adapter.openExternalLink(url);
    },
    [authFetch, adapter],
  );

  const startCheckout = useCallback(
    async (quantity = 1) => {
      setIsStartingCheckout(true);
      try {
        await openExternal(
          '/api/billing/checkout-session',
          { quantity },
          'Could not start checkout',
        );
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Could not start checkout');
      } finally {
        setIsStartingCheckout(false);
      }
    },
    [openExternal],
  );

  const openBillingPortal = useCallback(async () => {
    try {
      await openExternal('/api/billing/portal-session', {}, 'Could not open billing portal');
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Could not open billing portal');
    }
  }, [openExternal]);

  return (
    <BillingContext.Provider
      value={{
        entitlement,
        isLoading,
        refresh,
        startCheckout,
        openBillingPortal,
        actionError,
        isStartingCheckout,
      }}
    >
      {children}
    </BillingContext.Provider>
  );
}

export function useBilling(): BillingContextValue {
  const ctx = useContext(BillingContext);
  if (!ctx) throw new Error('useBilling must be used within BillingProvider');
  return ctx;
}
